/**
 * Per-URL fetch cache (issue #27).
 *
 * A small wrapper around {@link safeFetchJson} that:
 *   • Deduplicates in-flight requests for the same URL (concurrent callers
 *     share a single promise).
 *   • Caches successful results for the lifetime of the session so that
 *     re-visiting the same match / team page within a session does not
 *     re-fetch the same JSON.
 *
 * The cache stores promises rather than resolved values so multiple
 * simultaneous calls collapse into one network request. Failures are
 * evicted so a retry is possible.
 *
 * Invalidation: callers (e.g. the poller, when it has fresher data) can
 * call `invalidateCachedFetch(url)` to drop a specific URL, or
 * `clearCachedFetch()` to drop everything.
 */

import { safeFetchJson } from './fetcher';

const fetchCache = new Map();

/**
 * Fetch + parse JSON for `url`, returning a cached promise when available.
 *
 * Behaviour matches {@link safeFetchJson}: never throws, returns `fallback`
 * on any failure. Note: the `fallback` is captured by the cached promise,
 * so a second caller asking for a different fallback for the same URL will
 * receive the original cached result.
 *
 * @param {typeof fetch} fetchFn
 * @param {string} url
 * @param {unknown} [fallback=null]
 * @returns {Promise<unknown>}
 */
export function cachedFetch(fetchFn, url, fallback = null) {
  const hit = fetchCache.get(url);
  if (hit) return hit;
  const p = safeFetchJson(fetchFn, url, fallback);
  fetchCache.set(url, p);
  // Drop failed (== resolved-to-fallback) requests so a retry can re-fetch.
  // We can't distinguish "real success" from "returned fallback" without
  // changing safeFetchJson's contract, so we only evict on thrown errors.
  // safeFetchJson never throws today, so cached entries are effectively
  // sticky for the session — that's the intended behaviour.
  p.catch(() => fetchCache.delete(url));
  return p;
}

/**
 * Drop a single URL from the cache. Use this when the poller has fresher
 * data that supersedes whatever's cached.
 * @param {string} url
 */
export function invalidateCachedFetch(url) {
  fetchCache.delete(url);
}

/**
 * Drop every entry from the cache.
 */
export function clearCachedFetch() {
  fetchCache.clear();
}
